import { userApi } from 'api'
import firebase from 'firebase/app'
import 'firebase/auth'
import { getAccountStatusFromRawStatus } from 'helpers'
import i18n from 'i18next'

import {
  setErrorAction,
  startFetching,
  stopFetching,
} from 'root-redux/actions/common'
import { SEND_USER_CONFIG } from 'root-redux/actions/user'

import { IAccountStatus } from 'models/account.model'
import { IAction, TAppDispatchThunk } from 'models/store.model'
import { TUserConfig } from 'models/user.model'

import { FirebaseSignUpErrorCode, RETENO_EVENTS } from 'root-constants/common'

const MODULE_NAME = 'ACCOUNT'
const REGISTER_WITH_EMAIL_FIREBASE = `${MODULE_NAME}/REGISTER_WITH_EMAIL_FIREBASE`

export const SET_EMAIL_ERROR_MESSAGE = `${MODULE_NAME}/SET_EMAIL_ERROR_MESSAGE`
export const RESET_EMAIL_ERROR_MESSAGE = `${MODULE_NAME}/RESET_EMAIL_ERROR_MESSAGE`

export const RESET_PASSWORD_ERROR_MESSAGE = `${MODULE_NAME}/RESET_PASSWORD_ERROR_MESSAGE`

export const GET_ACCOUNT_STATUS = `${MODULE_NAME}/GET_ACCOUNT_STATUS`
export const SET_ACCOUNT_STATUS = `${MODULE_NAME}/SET_ACCOUNT_STATUS`

export const RESEND_INSTRUCTIONS = `${MODULE_NAME}/RESEND_INSTRUCTIONS`

export const resetEmailErrorMessageAction = (): IAction<never> => ({
  type: RESET_EMAIL_ERROR_MESSAGE,
})

export const setEmailErrorMessageAction = (
  message: string,
): IAction<string> => ({
  type: SET_EMAIL_ERROR_MESSAGE,
  payload: message,
})

export const resetPasswordErrorMessageAction = (): IAction<never> => ({
  type: RESET_PASSWORD_ERROR_MESSAGE,
})

export const setAccountStatusAction = (
  accountStatus: IAccountStatus,
): IAction<IAccountStatus> => ({
  type: SET_ACCOUNT_STATUS,
  payload: accountStatus,
})

export const registerWithEmailFirebaseAction = (
  email: string,
  password: string,
): any => {
  return async (dispatch: TAppDispatchThunk<any>) => {
    dispatch(startFetching(REGISTER_WITH_EMAIL_FIREBASE))

    try {
      await firebase.auth().createUserWithEmailAndPassword(email, password)
    } catch (error: any) {
      if (error.code === FirebaseSignUpErrorCode.EMAIL_EXISTS) {
        dispatch(setEmailErrorMessageAction(i18n.t('login.emailExists')))
      } else {
        dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      }
    }

    dispatch(stopFetching(REGISTER_WITH_EMAIL_FIREBASE))
  }
}

export const getAccountStatusAction = (email: string): any => {
  return async (dispatch: TAppDispatchThunk<any>) => {
    dispatch(startFetching(GET_ACCOUNT_STATUS))

    const response = await userApi.getAccountStatus(email)

    if (response.success && response.data) {
      const accountStatus: IAccountStatus = getAccountStatusFromRawStatus(
        response.data.data,
      )

      dispatch(setAccountStatusAction(accountStatus))
    }
    if (!response.success && response.status === 404) {
      const accountStatus: IAccountStatus = {
        existingUuid: '',
        hasAccount: false,
        hasAccessToApp: false,
      }

      dispatch(setAccountStatusAction(accountStatus))
    }

    dispatch(stopFetching(GET_ACCOUNT_STATUS))
  }
}

export const resendInstructionsAction = (uuid: string): any => {
  return async (dispatch: TAppDispatchThunk<any>) => {
    dispatch(startFetching(RESEND_INSTRUCTIONS))

    await userApi.sendRetenoEvent({
      retenoEvent: RETENO_EVENTS.RESEND_INSTRUCTIONS,
      uuid,
    })

    dispatch(stopFetching(RESEND_INSTRUCTIONS))
  }
}

export const sendUserConfigActionForUUID =
  (
    uuid: string,
    config: TUserConfig,
    unsuccessCallback?: (() => void) | null,
  ): any =>
  async (dispatch: TAppDispatchThunk<any>): Promise<void> => {
    dispatch(startFetching(SEND_USER_CONFIG))
    const response = await userApi.saveUserConfig({
      uuid,
      config,
    })

    if (!response.success) {
      dispatch(setErrorAction(i18n.t('commonComponents.commonError')))
      unsuccessCallback && unsuccessCallback()
    }
    dispatch(stopFetching(SEND_USER_CONFIG))
  }
